import { FormType } from 'generated/graphql';

export const appSwitchRoutes = {
  root: {
    path: '/',
    getUrl: () => '/',
  },
  authentication: {
    path: '/auth/*',
    getUrl: () => '/auth',
  },
  roles: {
    path: '/roles',
    getUrl: () => '/roles',
  },
  role: {
    path: '/roles/:roleId/stages',
    getUrl: (roleId: string) => `/roles/${roleId}/stages`,
  },
  candidatePortal: {
    path: '/roles/:roleId/portal',
    getUrl: (roleId: string) => `/roles/${roleId}/portal`,
  },
  roleStageActions: {
    path: '/roles/:roleId/stages/:stageId/actions',
    getUrl: (roleId: string, stageId: string) =>
      `/roles/${roleId}/stages/${stageId}/actions`,
  },
  roleStageContent: {
    path: '/roles/:roleId/stages/:stageId/content',
    getUrl: (roleId: string, stageId: string) =>
      `/roles/${roleId}/stages/${stageId}/content`,
  },
  // Intro (what the stage is, "Start now") — the portal deep-links here.
  roleStageEligibility: {
    path: '/roles/:roleId/stages/:stageId/eligibility',
    getUrl: (roleId: string, stageId: string) =>
      `/roles/${roleId}/stages/${stageId}/eligibility`,
  },
  roleStageEligibilityQuestions: {
    path: '/roles/:roleId/stages/:stageId/eligibility/questions',
    getUrl: (roleId: string, stageId: string) =>
      `/roles/${roleId}/stages/${stageId}/eligibility/questions`,
  },
  roleStageAdjustments: {
    path: '/roles/:roleId/stages/:stageId/adjustments/:step',
    getUrl: (roleId: string, stageId: string, step: string) =>
      `/roles/${roleId}/stages/${stageId}/adjustments/${step}`,
  },
  roleStageIdentityVerification: {
    path: '/roles/:roleId/stages/:stageId/identity-verification',
    getUrl: (roleId: string, stageId: string) =>
      `/roles/${roleId}/stages/${stageId}/identity-verification`,
  },
  roleStagePersonalityTest: {
    path: '/roles/:roleId/stages/:stageId/actions/personality-test',
    getUrl: (roleId: string, stageId: string) =>
      `/roles/${roleId}/stages/${stageId}/actions/personality-test`,
  },
  roleStageCognitiveTest: {
    path: '/roles/:roleId/stages/:stageId/actions/cognitive-test/:actionCandidateId',
    getUrl: (roleId: string, stageId: string, actionCandidateId: string) =>
      `/roles/${roleId}/stages/${stageId}/actions/cognitive-test/${actionCandidateId}`,
  },
  roleStageEnglishProficiencyTest: {
    path: '/roles/:roleId/stages/:stageId/actions/english-proficiency-test',
    getUrl: (roleId: string, stageId: string) =>
      `/roles/${roleId}/stages/${stageId}/actions/english-proficiency-test`,
  },
  roleStageRssAssessment: {
    path: '/roles/:roleId/stages/:stageId/actions/skills-self-assessment/:rssAssessmentCandidateId/:rssAssessmentType',
    getUrl: (
      roleId: string,
      stageId: string,
      rssAssessmentCandidateId: number | string,
      rssAssessmentType: string
    ) =>
      `/roles/${roleId}/stages/${stageId}/actions/skills-self-assessment/${rssAssessmentCandidateId}/${rssAssessmentType}`,
  },
  roleStageDeiIntro: {
    path: '/roles/:roleId/stages/:stageId/actions/dei',
    getUrl: (roleId: string, stageId: string) =>
      `/roles/${roleId}/stages/${stageId}/actions/dei`,
  },
  roleStageDeiForm: {
    path: '/roles/:roleId/stages/:stageId/actions/dei/form',
    getUrl: (roleId: string, stageId: string) =>
      `/roles/${roleId}/stages/${stageId}/actions/dei/form`,
  },
  roleStageForm: {
    path: '/roles/:roleId/stages/:stageId/actions/form/:formType',
    getUrl: (roleId: string, stageId: string, formType: FormType) =>
      `/roles/${roleId}/stages/${stageId}/actions/form/${formType}`,
  },
  roleStageCoderbyteAssessment: {
    path: '/roles/:roleId/stages/:stageId/actions/coding-assessment',
    getUrl: (roleId: string, stageId: string) =>
      `/roles/${roleId}/stages/${stageId}/actions/coding-assessment`,
  },
  roleStageThankYou: {
    path: '/roles/:roleId/stages/:stageId/actions/thank-you',
    getUrl: (roleId: string, stageId: string) =>
      `/roles/${roleId}/stages/${stageId}/actions/thank-you`,
  },
  externalService: {
    path: '/external-service',
    getUrl: () => '/external-service',
  },
  indirectInvitation: {
    path: '/inv',
    getUrl: () => '/inv',
  },
  companyNavigation: {
    path: '/company',
    getUrl: () => '/company',
  },
  personalSkillsProfile: {
    path: '/personal-skills-profile',
  },
  codingAssessmentThankYou: {
    // Fixed landing page Coderbyte returns every candidate to, configured as
    // the assessment's own `post_assessment_redirect_url` on their side (today
    // still via the monolith's `external-assessment/:platform/after-assessment`
    // callback, which redirects here). Coderbyte can only be given one URL per
    // assessment, so this path must not move without re-pointing them.
    path: '/external-assessment/thank-you',
    getUrl: () => '/external-assessment/thank-you',
  },
  aiInterview: {
    path: '/roles/:roleId/stages/:stageId/actions/ai-interview/:aiInterviewAssessmentStageActionCandidateId',
    getUrl: (
      roleId: string,
      stageId: string,
      aiInterviewAssessmentStageActionCandidateId: string
    ) =>
      `/roles/${roleId}/stages/${stageId}/actions/ai-interview/${aiInterviewAssessmentStageActionCandidateId}`,
  },
  /**
   * The AI interview's URL before it moved under its role and stage. Kept
   * only so tabs and history entries opened on the old address still land
   * on the interview; nothing builds this URL any more.
   */
  aiInterviewLegacy: {
    path: '/ai-interview/:aiInterviewAssessmentStageActionCandidateId',
  },
  automatedDecisionMaking: {
    path: '/automated-decision-making',
    getUrl: () => '/automated-decision-making',
  },
};

// Public, login-free namespace. Rendered by PublicSchedulingSwitch before the
// authenticated provider tree mounts (see App.tsx), so nothing here goes
// through PrivateRoute. BL-1513 adds the candidate booking page beneath it.
export const publicSchedulingRoutes = {
  root: {
    path: '/scheduling/*',
    getUrl: () => '/scheduling',
  },
  /** The per-candidate link: the token is minted when the offer is sent (BL-1512). */
  book: {
    path: '/scheduling/:token',
    getUrl: (token: string) => `/scheduling/${token}`,
  },
};

/**
 * A role's home screen: the candidate portal when the role has it enabled,
 * the legacy stage list otherwise. While the flag is still unknown (query in
 * flight or errored) it falls back to the stage list.
 */
export const getRoleHomeUrl = (
  roleId: string,
  candidatePortalEnabled: boolean | null | undefined
): string =>
  candidatePortalEnabled
    ? appSwitchRoutes.candidatePortal.getUrl(roleId)
    : appSwitchRoutes.role.getUrl(roleId);
